import { Button, Checkbox, DesignableObject, DesignerInterface, Textbox, Panel, Table, PropType, Component, KeyEvent, DataDisplayEvent, TableRow } from "@mcleod/components";
import { ButtonVariant } from "@mcleod/components";
import { ComponentPropDefinition, ComponentPropDefinitions } from "@mcleod/components/src/base/ComponentProps";
import { ReflectiveDialogs } from "@mcleod/components/src/base/ReflectiveDialogs";
import { fieldListSelector, stringArrayEditor } from "@mcleod/components/src/components/textbox/TextboxProps";
import { Alignment, Collection, getLogger, getThemeColor, Keys, VerticalAlignment } from "@mcleod/core";
import { DesignerDataSource } from "./DesignerDataSource";
import { designerCheckForPropChange } from "./UIDesignerUtil";

const log = getLogger("designer/ui/PropertiesTable");

interface EditorPanel extends Panel {
  editor: Component;
}

export class PropertiesTable extends Table {
  designer: DesignerInterface;
  expandedSections: Collection<boolean>;
  selectedPropIndex: number;
  selectedTable: Table;
  selectedComponents: Component[];
  propFilterFunction: (props: any) => void;

  constructor(designer: DesignerInterface, props?) {
    super({
      id: "tableProps",
      fillRow: true, fillHeight: true, padding: 0, headerVisible: false, rowAlign: VerticalAlignment.CENTER,
      expanderAlignment: Alignment.LEFT, rowSpacing: 0,
      columns: [
        { heading: { caption: "Property", paddingLeft: 4 }, cell: { field: "prop_name", height: 34, paddingLeft: 4, tooltip: cell => { if (cell.row.data.prop != null) return cell.row.data.prop.description; } } },
        { heading: { caption: "Value", paddingLeft: 8 }, cell: (row) => row.editor = this.createPropValueCell(row, "default", this) }
      ],
      ...props
    });
    this.addRowCreateListener(rowCreationEvent => this.onPropRowCreate(rowCreationEvent.getTableRow()));
    this.addRowDisplayListener(rowDisplayEvent => this.onPropRowDisplay(rowDisplayEvent.getTableRow()));
    this.addRowExpandListener(rowExpansionEvent => this._trackExpandedSections(rowExpansionEvent.getTableRow(), true));
    this.addRowCollapseListener(rowExpansionEvent => this._trackExpandedSections(rowExpansionEvent.getTableRow(), false));
    this.designer = designer;
    this.expandedSections = {};
  }

  createPropValueCell(row, filterCategory: string, table: Table) {
    const prop = row.data.prop;
    const result = new Panel({ id: "editor" + row.index, fillRow: true, padding: 0, borderLeftWidth: 1, borderLeftColor: "strokeSecondary", verticalAlign: VerticalAlignment.CENTER }) as EditorPanel;
    if (filterCategory != null && (prop == null || prop.category !== filterCategory))
      return result;
    let editor;
    if (prop.type === PropType.bool) {
      editor = new Checkbox({ paddingLeft: 8, rowBreak: false, fillRow: true });
      editor.addChangeListener((event) => {
        if (event.userInitiatedChange)
          this.designer.applyChangeToSelectedComponents(row.data, editor.checked);
      });
    }
    else {
      editor = new Textbox({ padding: 0, captionVisible: false, warningLabelVisible: false, borderWidth: 0, borderRadius: 0, height: "unset", dropDownAnchor: result, fillRow: true, rowBreak: false });
      editor.setProps({ paddingTop: 0, paddingBottom: 0, paddingLeft: 4 });
      editor.addDataDisplayListener((event: DataDisplayEvent) => {
        const value = event.rowData?.["value"];
        // these shouuld be ModelRows, but they aren't.  This was created before we standardized ModelRow.
        // so we reference the "value" directly
        if (value?.id != null)
          (event.target as Textbox).text = value.id;
      });
      if (typeof prop.items === "function")
        editor.items = () => prop.items(this.designer.getActiveTab());
      else
        editor.items = prop.items;
      if (prop.itemResultField != null)
        editor.onSelectItem = (input, selectedItem) => { return selectedItem[prop.itemResultField] };
      editor.addChangeListener((event) => {
        if (event.userInitiatedChange)
          this.designer.applyChangeToSelectedComponents(row.data, editor.text);
      });
      if (prop.category === "Events")
        editor.addDblClickListener(event => this.designer.addEventHandlerFunction(this.designer.selectedComponents[0], row.data.prop_name));
    }
    editor.rowBreak = false;
    result.add(editor);
    if (prop.editor != null) {
      if (prop.editor === stringArrayEditor) {
        if (prop.editorButtonImage == null)
          prop.editorButtonImage = "ellipsis";
        editor.enabled = false;
      }
      else if (prop.editor === fieldListSelector)
        editor.enabled = false;
      const button = new Button({ imageName: prop.editorButtonImage || "chevron", variant: ButtonVariant.round, color: "subtle.light" });
      button.addClickListener((event) => {
        let dialogContents;
        try {
          dialogContents = new prop.editor(editor.text, prop);
        } catch (err) {
          dialogContents = prop.editor(editor.text, prop);
        }
        ReflectiveDialogs.showDialog(dialogContents, { height: 600, width: 620, caption: dialogContents.caption, ...dialogContents.dialogProps })
          .then(() => {
            const result = dialogContents.getValue();
            if (result !== undefined) {
              this.designer.applyChangeToSelectedComponents(row.data, dialogContents.getValue());
              editor.text = result;
              this.designer.displayDataSourceTools();
            }
          });
      });
      result.add(button);
    }
    editor.index = row.index;
    editor.field = "value";
    editor.addFocusListener((event) => this.setSelectedPropIndex(table, row.index));
    editor.addBlurListener((event) => designerCheckForPropChange(this.designer, row.data, editor));
    editor.addKeyUpListener((event) => this.keyUp(event));
    result.editor = editor;
    return result;
  }

  // showOwnerFunctions(data, anchorPanel: EditorPanel) {  // not used, but not removing because we want something to do what this was supposed to do
  //   const owner = this.designer.getOwnerForActiveTab();
  //   if (owner != null) {
  //     const proto = Object.getPrototypeOf(owner);
  //     const items = [];
  //     for (const f of Object.getOwnPropertyNames(proto))
  //       if (f !== "constructor")
  //         items.push(f);
  //     const buttonShowCode = new Button({ caption: "Show function",  padding: 2, fillRow: true, borderWidth: 0, color: "primary" });
  //     buttonShowCode.addClickListener(() => this.designer.showCode());
  //     items.push(buttonShowCode);
  //     Overlay.showDropdown(anchorPanel, items, (value) => this.ownerSelected(data, value, anchorPanel.editor));
  //   }
  // }

  ownerSelected(data, value, editor) {
    editor.text = value;
    this.designer.applyChangeToSelectedComponents(data, value);
  }

  onPropRowCreate(row) {
    row.expandable = row.data.subRows != null;
    row.expandComponent = (table, row, expandComponent, expanding) => { return this.onPropRowExpand(table, row, expandComponent, expanding) };
  }

  private _trackExpandedSections(row: TableRow, isExpanding: boolean) {
    this.expandedSections[row.data.prop_name] = isExpanding;
  }

  onPropRowExpand(table, row, expandComponent, expanding) {
    if (!expanding)
      return;
    const result = new Table({
      id: "tableProps",
      fillRow: true, padding: 0, headerVisible: false, columnHeadingsVisible: false,
      //      height: row.data.subRows.length * 36 + 16,
      rowAlign: VerticalAlignment.CENTER, rowSpacing: 0, virtualized: false,
      columns: [
        { heading: { caption: "Property" }, cell: { field: "prop_display", height: 34, paddingLeft: 4, tooltip: cell => { if (cell.row.data.prop != null) return cell.row.data.prop.description; } } },
        { heading: { caption: "Value" }, cell: (row) => { row.editor = this.createPropValueCell(row, null, result); row.data.editor = row.editor; return row.editor } }
      ]
    });
    result.addRowDisplayListener(rowDisplayEvent => this.onPropRowDisplay(rowDisplayEvent.getTableRow()));
    for (const subRow of row.data.subRows) {
      result.addRow(subRow, null, { display: true });
    }
    return result;
  }

  setSelectedPropIndex(table, value) {
    if (this.selectedPropIndex >= 0 && this.selectedTable.rows[this.selectedPropIndex] != null && this.selectedTable.rows[this.selectedPropIndex].cells[0] != null) {
      this.selectedTable.rows[this.selectedPropIndex].cells[0].color = null;
      this.selectedTable.rows[this.selectedPropIndex].cells[0].backgroundColor = null;
    }
    if (table.rows[value] != null && table.rows[value].cells[0] != null) {
      table.rows[value].cells[0].color = "primary.reverse";
      table.rows[value].cells[0].backgroundColor = "primary.light";
    }
    this.selectedPropIndex = value;
    this.selectedTable = table;
  }

  categorizeProps(keys, props) {
    const result = {} as any;
    for (let i = 0; i < keys.length; i++) {
      const prop = props[keys[i]];
      if (prop != null) {
        if (prop.category == null)
          prop.category = "default";
        if (result[prop.category] == null)
          result[prop.category] = {};
        result[prop.category][keys[i]] = prop;
      }
    }
    return result;
  }

  /**
   * Returns the property names that all the selected components have in common.
   * @param {*} props
   * @returns
   */
  getCommonProperties(props: ComponentPropDefinitions) {
    const keys = Object.keys(props);
    for (let compIndex = 1; compIndex < this.selectedComponents.length; compIndex++)
      for (let i = keys.length - 1; i >= 0; i--)
        if (!(keys[i] in this.getSelectedComponent(compIndex)))
          keys.splice(i, 1);
    return keys;
  }

  /**
   * Removes any properties that are not intended to be shown in the designer.
   *    Controlled by the visibleInDesigner prop.
   * @param {*} props
   * @returns
   */
  removeNonDesignerProps(props: ComponentPropDefinitions) {
    for (const key in props) {
      if (props[key].visibleInDesigner === false) {
        log.debug("Removing prop from designer: " + key);
        delete props[key];
      }
    }
    return props;
  }

  /**
   * Returns the values that all the selected components have in common.
   * @param {*} keys
   * @returns
   */
  getCommonValues(keys: string[]) {
    const values = {};
    for (const key of keys) {
      values[key] = this.getSelectedComponent(0)[key];
      for (let compIndex = 1; compIndex < this.selectedComponents.length; compIndex++)
        for (const inner of keys)
          if (values[inner] !== this.getSelectedComponent(compIndex)[inner])
            values[inner] = null;
    }
    return values;
  }

  displayProperties(selectedComponents) {
    this.selectedComponents = selectedComponents;
    this.clearRows();
    if (this.selectedComponents.length === 0 || this.designer.getActiveTab() == null)
      return;
    const firstSel = this.getSelectedComponent(0);
    if (firstSel.getPropertyDefinitions == null) {
      log.info("The selected component", firstSel, "doesn't have a getPropertyDefinitions() function.");
      throw new Error("The selected component doesn't have a getPropertyDefinitions() function.  Check the console for more detailed info.");
    }
    let props = { ...firstSel.getPropertyDefinitions() };
    props = this.removeNonDesignerProps(props);
    const keys = this.getCommonProperties(props);
    const values = this.getCommonValues(keys);
    if (this.selectedComponents.length == 1 && firstSel !== this.designer.getActiveTab().designerPanel)
      keys.unshift("id");
    if (this.propFilterFunction != null) // let simplified version of designer (ListDefinitionEditor) filter out advanced props
      this.propFilterFunction(props);
    const cats = this.categorizeProps(keys, props);
    for (const key of Object.keys(cats.default).sort(this.idFirstSort)) {
      this.addRow({ prop_name: key, value: values[key], prop: cats.default[key] }, null, { display: true });
    }
    for (const key of Object.keys(cats).sort()) {
      if (key !== "default") {
        const subRows = [];
        const subProps = cats[key];
        for (const subKey of Object.keys(subProps))
          subRows.push({ prop_name: subKey, prop_display: props[subKey].displayName || subKey, value: values[subKey], prop: props[subKey] });
        this.addRow({ prop_name: key, value: values[key], subRows: subRows }, null, { display: true });
      }
    }
    for (const sectionName of Object.keys(this.expandedSections)) {
      if (this.expandedSections[sectionName] !== true)
        continue;
      for (const row of this.rows) {
        if (row.data["prop_name"] === sectionName) {
          row.expanded = true;
          break;
        }
      }
    }
  }

  redisplayProp(propName: string, value: string) {
    let selRow;
    for (const row of this.rows) {
      if (row.data.prop_name === propName)
        selRow = row;
      if (row.data.subRows != null)
        for (const sub of row.data.subRows)
          if (sub.prop_name === propName)
            selRow = sub;
    }
    if (selRow != null && selRow.editor != null && selRow.editor.editor != null)
      selRow.editor.editor.text = value;
  }

  getSelectedComponent(index: number): DesignableObject {
    let result = this.selectedComponents[index] as DesignableObject;
    if (result instanceof DesignerDataSource)
      result = result.designerDataSource;
    return result;
  }

  idFirstSort(item1: string, item2: string): number {
    if (item1 === "id")
      return -1;
    else if (item2 === "id")
      return 1;
    else
      return item1.localeCompare(item2)
  }

  onPropRowDisplay(row) {
    if (row.data.subRows != null) {
      row.element.style.backgroundColor = getThemeColor("table.headingRowBackgroundColor");
      row.element.style.color = getThemeColor("table.headingRowColor");
    }
    if (row.data.prop != null && !this.isDefaultValue(row.data.value, row.data.prop))
      row.cells[0].setProps({fontBold: true, color: "primary"});
    else if (row.data.prop == null && row.data.subRows != null) {
      for (const sub of row.data.subRows)
        if (!this.isDefaultValue(sub.value, sub.prop))
          row.cells[1].setProps({fontBold: true, color: "primary"});
    }
  }

  isDefaultValue(value, prop: ComponentPropDefinition) {
    if (value === undefined)
      return true;
    const defaultValue = this.selectedComponents[0].getPropertyDefaultValue(prop);
    if (value === false && defaultValue === undefined)
      return true;
    return value === defaultValue;
  }

  applyKeyToProp(key, prop) {
    let selRow, idRow, captionRow;
    let selExpandRow;
    for (const row of this.rows) {
      if (row.data.prop_name === prop)
        selRow = row;
      if (row.data.prop_name === "id")
        idRow = row;
      if (row.data.prop_name === "caption")
        captionRow = row;
      if (row.data.subRows != null) {
        for (const sub of row.data.subRows)
          if (sub.prop_name === prop) {
            selRow = sub;
            selExpandRow = row;
          }
      }
    }
    if (selExpandRow != null)
      log.debug("Sel expand", selExpandRow);
    const updRow = selRow || captionRow || idRow;
    // This didn't work when copying updRow. Each object is copied by reference. Would need to deep clone. Could do that using JSON.parse/stringify, but doesn't work in this situation.
    // object assign is a shallow copy.
    const updRowOrigData = Object.assign({}, updRow.data);
    if (updRow != null) {
      const editor = updRow["editor" + updRow.index];
      if (editor != null && editor.editor != null) {
        editor.editor.focus();
        if (key != null) {
          editor.editor.text = key;
          updRow.data.value = key;
          designerCheckForPropChange(this.designer, updRowOrigData, editor.editor);
          this.designer.applyChangeToSelectedComponents(updRow.data, updRow.data.value);
        }
      }
      else
        log.info("Null editor - trying to find this condition", this, prop, editor);
    }
  }

  keyUp(event: KeyEvent) {
    if (event.key === Keys.ESCAPE && this.selectedTable != null && (event?.domEvent?.currentTarget as HTMLElement)?.firstChild != null) {
      this.designer.applyChangeToSelectedComponents(this.selectedTable.rows[this.selectedPropIndex].data, this.selectedTable.rows[this.selectedPropIndex].data.value);
      const elem = event.domEvent.currentTarget as HTMLElement;
      const input = elem.firstChild.firstChild as HTMLInputElement;
      if (this.selectedTable.rows[this.selectedPropIndex].data.value != null)
        input.value = this.selectedTable.rows[this.selectedPropIndex].data.value;
      else
        input.value = "";
    }
  }
}
